import { FC, useEffect, useState } from "react";
import Carousel from "react-multi-carousel";
import { IPlaylists } from "../../types/playlists";
import PlaylistsCustomButtonGroup from "./components/PlaylistsCustomButtonGroup";
import PlaylistCard from "./components/PlaylistCard";

type Props = {
  title: string;
  playlists: IPlaylists[];
};

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 4,
  },
  tablet: {
    breakpoint: { max: 1024, min: 768 },
    items: 4,
  },
  "2xl": {
    breakpoint: { max: 768, min: 650 },
    items: 4.2,
  },
  xl: {
    breakpoint: { max: 650, min: 500 },
    items: 3.7,
  },
  lg: {
    breakpoint: { max: 500, min: 448 },
    items: 3.6,
  },
  md: {
    breakpoint: { max: 448, min: 420 },
    items: 3,
  },
  sm: {
    breakpoint: { max: 420, min: 390 },
    items: 3,
  },
  xs: {
    breakpoint: { max: 390, min: 370 },
    items: 3,
    paritialVisibilityGutter: 0,
  },
  sx: {
    breakpoint: { max: 370, min: 0 },
    items: 2.7,
  },
};

const MAX_ITEMS_STATIC_DESKTOP = 4;
const MAX_ITEMS_STATIC_MOBILE = 3;

const PlaylistsCarousel: FC<Props> = ({ title, playlists }) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  if (!playlists.length) return null;

  const maxStaticItems = isMobile
    ? MAX_ITEMS_STATIC_MOBILE
    : MAX_ITEMS_STATIC_DESKTOP;

  return (
    <div className="px-5 md:px-0">
      <div>
        <h4 className="text-xl text-black font-bold">{title}</h4>
      </div>

      <div className="md:pr-5 w-full overflow-hidden">
        {playlists.length <= maxStaticItems ? (
          <div className="flex justify-center gap-4 flex-wrap">
            {playlists.map((playlist, index) => (
              <PlaylistCard
                key={index}
                icon={playlist.imagePath}
                name={playlist.name}
                publicUrl={playlist.publicUrl}
                spotifyUrl={playlist.commnetByAdmin}
              />
            ))}
          </div>
        ) : (
          <Carousel
            itemAriaLabel={"0"}
            arrows={false}
            customButtonGroup={
              <PlaylistsCustomButtonGroup
                responsive={responsive}
                playlists={playlists}
              />
            }
            slidesToSlide={1}
            additionalTransfrom={0}
            autoPlaySpeed={3000}
            centerMode={false}
            draggable
            focusOnSelect={false}
            infinite
            keyBoardControl
            minimumTouchDrag={100}
            pauseOnHover
            renderArrowsWhenDisabled={false}
            renderButtonGroupOutside={false}
            renderDotsOutside={false}
            className="max-w-screen"
            autoPlay={true}
            rewind={false}
            rewindWithAnimation={false}
            rtl={false}
            shouldResetAutoplay
            showDots={false}
            swipeable
            itemClass="image-item"
            responsive={responsive}
            removeArrowOnDeviceType={[
              "sx",
              "xs",
              "sm",
              "md",
              "lg",
              "xl",
              "2xl",
            ]}
          >
            {playlists.map((playlist, index) => (
              <PlaylistCard
                key={index}
                icon={playlist.imagePath}
                name={playlist.name}
                publicUrl={playlist.publicUrl}
                spotifyUrl={playlist.commnetByAdmin}
              />
            ))}
          </Carousel>
        )}
      </div>

      <div className="border-b border-light_gray pt-[4rem] mr-5 mb-[4rem]"></div>
    </div>
  );
};

export default PlaylistsCarousel;
